import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Modal } from "semantic-ui-react";

import { useSelectedDealer } from "components";
import { createFormHelper } from "lib/Form";
import "modules/QuestionsDashboard/components/CreateQuestionModal/CreateQuestionModal.scss";
import { useCreateQuestionMutation } from "modules/QuestionsDashboard/components/CreateQuestionModal/hooks";

import type { Question, QuestionID } from "models";

type CreateQuestionFormValues = Pick<Question, "title">;

type CreateQuestionModalProps = {
  isOpen: boolean;
  isDealerQuestion: boolean;
  onClose: () => void;
  onCreate: (questionID: QuestionID) => void;
};

const FormHelper = createFormHelper<CreateQuestionFormValues>();

export const CreateQuestionModal = ({ isOpen, isDealerQuestion, onClose, onCreate }: CreateQuestionModalProps) => {
  const { t } = useTranslation();

  const [hasErrors, setHasErrors] = useState(false);

  const { selectedDealer } = useSelectedDealer();
  const { mutateAsync: createQuestion, isPending } = useCreateQuestionMutation();

  const handleClose = () => {
    setHasErrors(false);
    onClose();
  };

  const handleSubmit = async ({ title }: CreateQuestionFormValues) => {
    const trimmedTitle = title?.trim();
    if (!trimmedTitle) {
      setHasErrors(true);
      return;
    }

    const { id } = await createQuestion({ title: trimmedTitle, dealer_id: isDealerQuestion ? (selectedDealer?.id ?? null) : null });
    setHasErrors(false);
    onCreate(id);
  };

  return (
    <Modal size="tiny" className="CreateQuestionModal" open={isOpen} onClose={handleClose} closeOnDimmerClick={false}>
      <Modal.Header>
        {isDealerQuestion ? t("v8_create_dealer_question", "Create dealer question") : t("v8_create_default_question", "Create default question")}
      </Modal.Header>

      <FormHelper.Form onSubmit={handleSubmit} defaultValues={{ title: "" }}>
        <Modal.Content>
          <Form.Field required error={hasErrors}>
            <FormHelper.Field.Input name="title" inputProps={{ placeholder: t("v8_question", "Question"), fluid: true }} />
          </Form.Field>
        </Modal.Content>

        <Modal.Actions>
          <Button type="button" className="light" onClick={handleClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>

          <Button type="submit" color="green" disabled={isPending} loading={isPending}>
            {t("v8_confirm", "Confirm")}
            <Icon className="check" />
          </Button>
        </Modal.Actions>
      </FormHelper.Form>
    </Modal>
  );
};
